import { useEffect, useState } from "react";
import { api, formatApiError, ksh } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Wallet, Users, ChevronDown, ChevronUp, X, Save, Building2, FileText } from "lucide-react";
import { API } from "@/lib/api";

const PLAN_LABELS = {
  starter: "Starter",
  pro: "Pro",
  enterprise: "Enterprise",
  custom: "Custom",
};
const PLAN_ACCENTS = {
  starter: "#0EA5E9",
  pro: "#059669",
  enterprise: "#6366F1",
  custom: "#525252",
};

export default function OfficePacksTab() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [detailMap, setDetailMap] = useState({}); // biz_id → full detail

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/business-accounts");
      setAccounts(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not load business accounts");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const expand = async (biz) => {
    const wasOpen = expanded === biz.id;
    setExpanded(wasOpen ? null : biz.id);
    if (!wasOpen && !detailMap[biz.id]) {
      try {
        const { data } = await api.get(`/admin/business-accounts/${biz.id}`);
        setDetailMap((m) => ({ ...m, [biz.id]: data }));
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail) || "Could not load details");
      }
    }
  };

  const refreshDetail = async (bizId) => {
    try {
      const { data } = await api.get(`/admin/business-accounts/${bizId}`);
      setDetailMap((m) => ({ ...m, [bizId]: data }));
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not refresh");
    }
  };

  return (
    <div className="space-y-5" data-testid="office-packs-tab">
      <div className="bg-white border border-[#E5E5E5] p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252] mb-1">§ B2B Wallet</div>
            <h2 className="font-display font-black text-2xl">Office Packs</h2>
            <p className="text-xs text-[#525252] mt-1">
              Prepaid business accounts. Each gets a wallet, a flat discount on every order, and one or more linked employee logins. Orders auto-settle from the wallet when balance covers the total.
            </p>
          </div>
          <button
            onClick={() => setCreating(true)}
            data-testid="office-pack-new"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A0A0A] text-white text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#059669]"
          >
            <Plus className="w-3.5 h-3.5" /> New business account
          </button>
        </div>
      </div>

      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin text-[#059669]" />
      ) : accounts.length === 0 ? (
        <div className="border-2 border-dashed border-[#E5E5E5] bg-white p-10 text-center" data-testid="office-pack-empty">
          <Building2 className="w-10 h-10 text-[#059669] mx-auto mb-3" />
          <div className="font-display font-bold text-xl">No business accounts yet</div>
          <div className="text-sm text-[#525252] mt-1">Create one to start onboarding a corporate client.</div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="office-pack-list">
          {accounts.map((b) => (
            <AccountCard
              key={b.id}
              account={b}
              expanded={expanded === b.id}
              detail={detailMap[b.id]}
              onToggle={() => expand(b)}
              onRefresh={() => refreshDetail(b.id)}
            />
          ))}
        </div>
      )}

      {creating && (
        <CreateBusinessModal
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); load(); }}
        />
      )}
    </div>
  );
}

function AccountCard({ account, expanded, detail, onToggle, onRefresh }) {
  const planAccent = PLAN_ACCENTS[account.plan] || "#525252";
  return (
    <div
      className="bg-white border border-[#E5E5E5]"
      data-testid={`office-pack-card-${account.id}`}
      style={{ borderLeft: `4px solid ${planAccent}` }}
    >
      <div className="p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-display font-bold text-lg">{account.name}</span>
            <span
              className="px-2 py-0.5 border text-[9px] uppercase tracking-[0.2em] font-bold"
              style={{ borderColor: planAccent, color: planAccent }}
            >
              {PLAN_LABELS[account.plan] || account.plan}
            </span>
            {!account.active && (
              <span className="px-2 py-0.5 border border-[#DC2626] text-[9px] uppercase tracking-[0.2em] font-bold text-[#DC2626]">
                Deactivated
              </span>
            )}
          </div>
          <div className="text-xs text-[#525252] mono">{account.admin_email}</div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <div className="text-[9px] uppercase tracking-[0.2em] text-[#525252]">Wallet</div>
            <div className="font-display font-black text-xl mono" data-testid={`office-pack-balance-${account.id}`}>
              {ksh(account.wallet_kes || 0)}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[9px] uppercase tracking-[0.2em] text-[#525252]">Discount</div>
            <div className="font-display font-black text-xl mono">{(account.discount_pct || 0).toFixed(0)}%</div>
          </div>
          <div className="text-right">
            <div className="text-[9px] uppercase tracking-[0.2em] text-[#525252]">Members</div>
            <div className="font-display font-black text-xl mono">{account.member_count ?? 0}</div>
          </div>
          <button
            onClick={onToggle}
            data-testid={`office-pack-toggle-${account.id}`}
            className="w-8 h-8 border border-[#E5E5E5] hover:border-[#0A0A0A] flex items-center justify-center"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-[#E5E5E5] bg-[#FAFAFA] p-4 space-y-4">
          {!detail ? (
            <Loader2 className="w-4 h-4 animate-spin text-[#525252]" />
          ) : (
            <ExpandedAccount detail={detail} onRefresh={onRefresh} />
          )}
        </div>
      )}
    </div>
  );
}

function ExpandedAccount({ detail, onRefresh }) {
  return (
    <div className="grid md:grid-cols-3 gap-4">
      <SettingsPanel detail={detail} onRefresh={onRefresh} />
      <MembersPanel detail={detail} onRefresh={onRefresh} />
      <TopUpPanel detail={detail} onRefresh={onRefresh} />
      <div className="md:col-span-3">
        <StatementPanel detail={detail} />
      </div>
      <div className="md:col-span-3">
        <TransactionList transactions={detail.transactions || []} />
      </div>
    </div>
  );
}

function StatementPanel({ detail }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // current month default
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("ctp_token");
      const url = `${API}/admin/business-accounts/${detail.id}/statement.pdf?year=${year}&month=${month}`;
      const res = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `statement-${detail.name.replace(/\s+/g, "_")}-${year}-${String(month).padStart(2, "0")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
      toast.success("Statement downloaded");
    } catch (e) {
      toast.error(formatApiError(e?.message) || "Could not generate statement");
    } finally {
      setBusy(false);
    }
  };

  // Last 12 months in (year, month) tuples for the dropdown
  const months = [];
  const cur = new Date(today.getFullYear(), today.getMonth(), 1);
  for (let i = 0; i < 12; i++) {
    const d = new Date(cur.getFullYear(), cur.getMonth() - i, 1);
    months.push({ y: d.getFullYear(), m: d.getMonth() + 1, label: d.toLocaleString("en-KE", { month: "long", year: "numeric" }) });
  }

  const selected = `${year}-${month}`;

  return (
    <div className="bg-white border border-[#E5E5E5] p-4 flex flex-wrap items-center gap-3" data-testid={`office-pack-statement-${detail.id}`}>
      <FileText className="w-4 h-4 text-[#525252]" />
      <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252] flex-shrink-0">
        Monthly statement
      </div>
      <select
        value={selected}
        onChange={(e) => {
          const [y, m] = e.target.value.split("-").map(Number);
          setYear(y); setMonth(m);
        }}
        data-testid={`office-pack-statement-period-${detail.id}`}
        className="px-3 py-2 border border-[#0A0A0A] text-xs font-bold bg-white"
      >
        {months.map((mm) => (
          <option key={`${mm.y}-${mm.m}`} value={`${mm.y}-${mm.m}`}>{mm.label}</option>
        ))}
      </select>
      <button
        onClick={download}
        disabled={busy}
        data-testid={`office-pack-statement-download-${detail.id}`}
        className="inline-flex items-center gap-2 px-4 py-2 bg-[#059669] text-white text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#047857] disabled:opacity-50"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
        Download PDF
      </button>
      <p className="text-[10px] text-[#525252] flex-1 min-w-[180px]">
        Opening balance, every wallet transaction, orders billed, closing balance — share with the business&apos;s finance team.
      </p>
    </div>
  );
}

function SettingsPanel({ detail, onRefresh }) {
  const [plan, setPlan] = useState(detail.plan);
  const [discount, setDiscount] = useState(detail.discount_pct);
  const [active, setActive] = useState(detail.active ?? true);
  const [lowBalThreshold, setLowBalThreshold] = useState(detail.low_balance_threshold ?? 500);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/business-accounts/${detail.id}`, {
        plan,
        discount_pct: parseFloat(discount),
        active,
        low_balance_threshold: parseFloat(lowBalThreshold) || 0,
      });
      toast.success("Settings updated");
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-[#E5E5E5] p-4 space-y-3" data-testid={`office-pack-settings-${detail.id}`}>
      <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252]">Settings</div>
      <div>
        <label className="block text-[9px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">Plan</label>
        <select
          value={plan}
          onChange={(e) => setPlan(e.target.value)}
          data-testid={`office-pack-plan-${detail.id}`}
          className="w-full px-3 py-2 border border-[#0A0A0A] text-sm font-bold bg-white"
        >
          {Object.entries(PLAN_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-[9px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">Discount %</label>
        <input
          type="number"
          min={0}
          max={100}
          step={0.5}
          value={discount}
          onChange={(e) => setDiscount(e.target.value)}
          data-testid={`office-pack-discount-${detail.id}`}
          className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-sm mono"
        />
      </div>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
          data-testid={`office-pack-active-${detail.id}`}
        />
        <span>Account is active</span>
      </label>
      <div>
        <label className="block text-[9px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">Low-balance alert · KES</label>
        <input
          type="number"
          min={0}
          max={1000000}
          value={lowBalThreshold}
          onChange={(e) => setLowBalThreshold(e.target.value)}
          data-testid={`office-pack-low-threshold-${detail.id}`}
          className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-sm mono"
        />
        <p className="text-[10px] text-[#525252] mt-1">Members get an email when the wallet first dips below this. Resets after the next top-up over the threshold.</p>
      </div>
      <button
        onClick={save}
        disabled={busy}
        data-testid={`office-pack-save-${detail.id}`}
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#0A0A0A] text-white text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#059669] disabled:opacity-50"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
        Save changes
      </button>
    </div>
  );
}

function MembersPanel({ detail, onRefresh }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!email) return;
    setBusy(true);
    try {
      await api.post(`/admin/business-accounts/${detail.id}/members`, { email });
      toast.success("Member added");
      setEmail("");
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not add member");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (userId) => {
    try {
      await api.delete(`/admin/business-accounts/${detail.id}/members/${userId}`);
      toast.success("Member removed");
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not remove member");
    }
  };

  return (
    <div className="bg-white border border-[#E5E5E5] p-4 space-y-3" data-testid={`office-pack-members-${detail.id}`}>
      <div className="flex items-center gap-2">
        <Users className="w-3.5 h-3.5 text-[#525252]" />
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252]">
          Members · {detail.members?.length || 0}
        </div>
      </div>
      <div className="space-y-1.5 max-h-44 overflow-y-auto">
        {(detail.members || []).length === 0 && (
          <div className="text-xs text-[#525252] italic">No members yet.</div>
        )}
        {(detail.members || []).map((m) => (
          <div key={m.id} className="flex items-center justify-between gap-2 text-xs border border-[#E5E5E5] bg-[#FAFAFA] px-2 py-1.5">
            <div className="min-w-0">
              <div className="font-bold truncate">{m.name || m.email}</div>
              <div className="mono text-[10px] text-[#525252] truncate">{m.email}</div>
            </div>
            <button
              onClick={() => remove(m.id)}
              title="Remove from business"
              data-testid={`office-pack-member-remove-${m.id}`}
              className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#DC2626] hover:underline"
            >
              remove
            </button>
          </div>
        ))}
      </div>
      <div className="space-y-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="employee@company.com"
          data-testid={`office-pack-member-email-${detail.id}`}
          className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-sm"
        />
        <button
          onClick={add}
          disabled={busy || !email}
          data-testid={`office-pack-member-add-${detail.id}`}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 border border-[#0A0A0A] text-[#0A0A0A] text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#0A0A0A] hover:text-white disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          Add member
        </button>
        <p className="text-[10px] text-[#525252]">User must have an existing CTP account.</p>
      </div>
    </div>
  );
}

function TopUpPanel({ detail, onRefresh }) {
  const [amount, setAmount] = useState("");
  const [receipt, setReceipt] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!amount || parseFloat(amount) <= 0) return;
    setBusy(true);
    try {
      await api.post(`/admin/business-accounts/${detail.id}/topup`, {
        amount: parseFloat(amount),
        mpesa_receipt: receipt || null,
        note: note || null,
      });
      toast.success(`Wallet topped up by KES ${amount}`);
      setAmount(""); setReceipt(""); setNote("");
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Top-up failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-[#E5E5E5] p-4 space-y-3" data-testid={`office-pack-topup-${detail.id}`}>
      <div className="flex items-center gap-2">
        <Wallet className="w-3.5 h-3.5 text-[#525252]" />
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252]">Manual top-up</div>
      </div>
      <div>
        <label className="block text-[9px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">Amount (KES)</label>
        <input
          type="number"
          min={1}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="e.g. 5000"
          data-testid={`office-pack-topup-amount-${detail.id}`}
          className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-sm mono"
        />
      </div>
      <div>
        <label className="block text-[9px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">M-Pesa receipt (optional)</label>
        <input
          type="text"
          value={receipt}
          onChange={(e) => setReceipt(e.target.value.toUpperCase())}
          placeholder="e.g. NLJ7RT61SV"
          data-testid={`office-pack-topup-receipt-${detail.id}`}
          className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-sm mono"
        />
      </div>
      <div>
        <label className="block text-[9px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">Note</label>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Feb cycle"
          data-testid={`office-pack-topup-note-${detail.id}`}
          className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-sm"
        />
      </div>
      <button
        onClick={submit}
        disabled={busy || !amount}
        data-testid={`office-pack-topup-submit-${detail.id}`}
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#059669] text-white text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#047857] disabled:opacity-50"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
        Add to wallet
      </button>
    </div>
  );
}

function TransactionList({ transactions }) {
  if (!transactions.length) return null;
  return (
    <div className="bg-white border border-[#E5E5E5] p-4">
      <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252] mb-3">Recent activity</div>
      <ul className="space-y-1.5 max-h-64 overflow-y-auto">
        {transactions.map((t) => (
          <li key={t.id} className="flex items-baseline justify-between gap-3 text-xs border-b border-[#F5F5F5] pb-1.5 last:border-b-0">
            <div className="min-w-0">
              <span className="mono text-[10px] text-[#525252]">
                {new Date(t.created_at).toLocaleString("en-KE", { dateStyle: "short", timeStyle: "short" })}
              </span>{" "}
              <span className={`px-1.5 py-0.5 border text-[9px] uppercase tracking-[0.15em] font-bold ${t.kind === "topup" ? "border-[#059669] text-[#059669]" : "border-[#DC2626] text-[#DC2626]"}`}>
                {t.kind}
              </span>{" "}
              <span className="text-[#525252]">{t.note}</span>
              {t.mpesa_receipt && <span className="mono text-[#525252] ml-1">· {t.mpesa_receipt}</span>}
            </div>
            <div className="text-right whitespace-nowrap">
              <div className={`font-bold mono ${t.kind === "topup" ? "text-[#059669]" : "text-[#DC2626]"}`}>
                {t.kind === "topup" ? "+" : "−"}{ksh(Math.abs(t.amount))}
              </div>
              <div className="mono text-[10px] text-[#525252]">balance {ksh(t.balance_after)}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CreateBusinessModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [plan, setPlan] = useState("starter");
  const [discount, setDiscount] = useState("");
  const [initialWallet, setInitialWallet] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name || !email) {
      toast.error("Name and admin email are required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name,
        admin_email: email,
        plan,
        initial_wallet: initialWallet ? parseFloat(initialWallet) : 0,
      };
      if (discount !== "") payload.discount_pct = parseFloat(discount);
      await api.post("/admin/business-accounts", payload);
      toast.success("Business account created");
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not create");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="office-pack-modal">
      <div className="bg-white border-2 border-[#0A0A0A] w-full max-w-md p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252]">§ New business</div>
            <h3 className="font-display font-black text-2xl">Office Pack</h3>
          </div>
          <button onClick={onClose} className="text-[#525252] hover:text-[#0A0A0A]" data-testid="office-pack-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div>
          <label className="block text-[9px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">Business name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Nairobi Law Firm"
            data-testid="office-pack-new-name"
            className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-sm"
          />
        </div>
        <div>
          <label className="block text-[9px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">Admin contact email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="contact@company.co.ke"
            data-testid="office-pack-new-email"
            className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-sm mono"
          />
          <p className="text-[10px] text-[#525252] mt-1">If a user with this email already exists they&apos;re auto-linked.</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[9px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">Plan</label>
            <select
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
              data-testid="office-pack-new-plan"
              className="w-full px-3 py-2 border border-[#0A0A0A] text-sm font-bold bg-white"
            >
              {Object.entries(PLAN_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[9px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">Discount % (optional)</label>
            <input
              type="number"
              min={0}
              max={100}
              step={0.5}
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
              placeholder="Plan default"
              data-testid="office-pack-new-discount"
              className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-sm mono"
            />
          </div>
        </div>
        <div>
          <label className="block text-[9px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">Initial wallet load (optional)</label>
          <input
            type="number"
            min={0}
            value={initialWallet}
            onChange={(e) => setInitialWallet(e.target.value)}
            placeholder="e.g. 5000"
            data-testid="office-pack-new-wallet"
            className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-sm mono"
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-[#E5E5E5] text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] hover:border-[#0A0A0A] hover:text-[#0A0A0A]"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy}
            data-testid="office-pack-new-submit"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A0A0A] text-white text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#059669] disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Create
          </button>
        </div>
      </div>
    </div>
  );
}
